import React, { useId } from 'react';
import { RsIcon } from './roomShellStyle';
import { panelFor } from '../../utils/roomPanels';

// 🧱 One rail of the frame (mockup frame A): a stack of panels, each with a
// quiet header (its icon, name, then Move · Remove on hover or focus, and the
// fold chevron), ending in a dashed "+ Add a tool". An empty rail is only a
// round "+". A narrow desktop (768-1099) gets the MINI rail: one icon per
// panel (each opens its panel in a sheet) and the round "+", so the chat
// column keeps its width.
//
//   side        'left' | 'right'
//   entries     [{ id, collapsed }]  this rail's placed + drawable panels
//   renderBody  (panel, { side }) => node   the frame draws each body
//   onAdd       (side) => void       the "+" (opens the shelf for this rail)
//   onToggle / onMove / onRemove  (id) => void
//   mini        bool; onOpenPanel (id) => void

const OTHER = { left: 'right', right: 'left' };

export function RoomPanelBox({ panel, collapsed = false, side, onToggle, onMove, onRemove, children }) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const titleId = `rs-pt-${uid}`;
  const bodyId = `rs-pb-${uid}`;
  return (
    <section className="rs-pn" data-rs-panel={panel.id} data-collapsed={collapsed ? '1' : '0'} aria-labelledby={titleId}>
      <div className="rs-pnh">
        <span className="rs-pnic" aria-hidden="true"><RsIcon name={panel.icon} size={15} /></span>
        <div className="rs-pnt" id={titleId} role="heading" aria-level={3}>{panel.label}</div>
        <span className="rs-sp" />
        <span className="rs-ctl">
          {onMove ? (
            <button type="button" className="rs-ib" data-prop-ui="" data-rs-move={panel.id}
              aria-label={`Move ${panel.label} to the ${OTHER[side]} side`} title={`Move to the ${OTHER[side]} side`} onClick={() => onMove(panel.id)}>
              <RsIcon name="swap" size={14} />
            </button>
          ) : null}
          {onRemove ? (
            <button type="button" className="rs-ib" data-prop-ui="" data-rs-remove={panel.id}
              aria-label={`Remove ${panel.label}`} title="Remove" onClick={() => onRemove(panel.id)}>
              <RsIcon name="x" size={14} />
            </button>
          ) : null}
        </span>
        <button type="button" className="rs-ib" data-prop-ui="" data-rs-fold={panel.id}
          aria-label={`${collapsed ? 'Show' : 'Hide'} ${panel.label}`} aria-expanded={!collapsed} aria-controls={collapsed ? undefined : bodyId}
          onClick={() => onToggle && onToggle(panel.id)}>
          <RsIcon name={collapsed ? 'chevD' : 'chevU'} size={15} />
        </button>
      </div>
      {collapsed ? null : <div className="rs-pnb" id={bodyId}>{children}</div>}
    </section>
  );
}

export default function RoomRail({
  side, entries = [], renderBody, onAdd, onToggle, onMove, onRemove, mini = false, onOpenPanel = null, openId = null,
}) {
  const label = side === 'left' ? 'Left tools' : 'Right tools';
  const panels = entries.map((e) => ({ entry: e, panel: panelFor(e.id) })).filter((x) => x.panel);
  const addLabel = `Add a tool to the ${side} side`;
  if (mini || !panels.length) {
    return (
      <aside className="rs-rail" data-side={side} data-rs-rail={side} data-empty={panels.length ? '0' : '1'} data-mini={mini ? '1' : '0'} aria-label={label}>
        {mini ? panels.map(({ panel }) => (
          <button key={panel.id} type="button" className="rs-mini" data-prop-ui="" data-rs-mini={panel.id}
            aria-label={panel.label} title={panel.label} aria-haspopup={panel.mode === 'inline' ? 'dialog' : undefined}
            aria-expanded={panel.mode === 'inline' ? openId === panel.id : undefined} onClick={() => onOpenPanel && onOpenPanel(panel.id)}>
            <RsIcon name={panel.icon} size={19} />
          </button>
        )) : null}
        <button type="button" className="rs-add" data-round="1" data-prop-ui="" data-rs-add={side} aria-label={addLabel} title="Add a tool" onClick={() => onAdd && onAdd(side)}>
          <RsIcon name="plus" size={18} />
        </button>
      </aside>
    );
  }
  return (
    <aside className="rs-rail" data-side={side} data-rs-rail={side} data-empty="0" data-mini="0" aria-label={label}>
      {panels.map(({ entry, panel }) => (
        <RoomPanelBox key={panel.id} panel={panel} collapsed={!!entry.collapsed} side={side} onToggle={onToggle} onMove={onMove} onRemove={onRemove}>
          {renderBody ? renderBody(panel, { side }) : null}
        </RoomPanelBox>
      ))}
      <button type="button" className="rs-add" data-prop-ui="" data-rs-add={side} aria-label={addLabel} onClick={() => onAdd && onAdd(side)}>
        <RsIcon name="plus" size={15} />Add a tool
      </button>
    </aside>
  );
}
